import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import Loading from "../../components/common/Loading";
import { SubscriberService } from "../../../services/SubscriberService";
import { ISubscriber } from "../../../models/Interfaces";
import { useConfirm } from "../../../config/ConfirmProvider";
import { CellProps, Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEdit, faFileUpload, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import FilteredTable from "../../components/common/FilteredTable";
import AppModal from "../../components/common/AppModal";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./manageSubscribers.module.scss";

const ManageSubscribers = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [subscribers, setSubscribers] = useState<Array<ISubscriber>>([]);
  const [selectedSub, setSelectedSub] = useState<ISubscriber | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);

  const subSvcRef = useRef<SubscriberService | null>(null);
  const subFormRef = useRef<HTMLFormElement | null>(null);
  const subFvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();
  const nav = useNavigate();

  const columns: Column<ISubscriber>[] = useMemo(() => [
    {
      Header: "Name",
      accessor: "Name"
    },
    {
      Header: "Email Address",
      accessor: "EmailAddress"
    },
    {
      Header: "Newsletter Sub",
      Cell: ({ row }: CellProps<ISubscriber>) => {
        const { Id, Newsletter } = row.original;
        return (
          <span
            className={styles.statusCell}
            title={Newsletter ? "click to unsubscribe from newsletters" : "click to subscribe to newsletters"}
            onClick={() => handleTableItemChange(Id, "Newsletter", !Newsletter)}
          >
            <span className={Newsletter ? styles.statusBadgeOn : styles.statusBadgeOff}>
              {Newsletter ? "Subscribed" : "Unsubscribed"}
            </span>
          </span>
        );
      }
    },
    {
      Header: "Enabled",
      Cell: ({ row }: CellProps<ISubscriber>) => {
        const { Id, Enabled } = row.original;
        return (
          <span
            className={styles.statusCell}
            title={Enabled ? "click to disable subscriber" : "click to enable subscriber"}
            onClick={() => handleTableItemChange(Id, "Enabled", !Enabled)}
          >
            <span className={Enabled ? styles.statusBadgeOn : styles.statusBadgeOff}>
              {Enabled ? "Enabled" : "Disabled"}
            </span>
          </span>
        );
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<ISubscriber>) => (
        <div className="user-result-actions">
          <Button variant="light" onClick={() => editSubscriber(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit subscriber">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteSubscriber(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete subscriber">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [subscribers]);

  const getSubscribers = async (): Promise<void> => {
    if (subSvcRef.current) {
      const res = await subSvcRef.current.getSubscribers();
      setSubscribers(res);
    }
  };

  const addSubscriber = (): void => {
    if (subSvcRef.current) {
      const newsub = subSvcRef.current.newSubscriber();
      if (newsub) {
        setSelectedSub(newsub);
        setShowDetail(true);
      }
    }
  };

  const editSubscriber = (sub: ISubscriber): void => {
    setSelectedSub(sub);
    setShowDetail(true);
  };

  const saveSubscriber = async (): Promise<void> => {
    if (selectedSub && subSvcRef.current && subFvRef.current) {
      const validres = await subFvRef.current.validate();

      if (validres === "Valid") {
        const newsub = selectedSub.Id <= 0;
        const res = await subSvcRef.current.saveSubscriber(selectedSub, newsub);

        if (res) {
          setSubscribers(prev =>
            prev.some(sub => sub.Id === res.Id)
              ? prev.map(sub => (sub.Id === res.Id ? res : sub))
              : [...prev, res]
          );
        }
        closeDetail();
      }
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedSub, (item) => item.Id, () => closeDetail());

  const deleteSubscriber = async (sub: ISubscriber): Promise<void> => {
    if (subSvcRef.current && sub) {
      const confirmed = await confirmation.requestConfirmation(`Delete <b>${sub.Name}</b> from your site subscribers?<br /><b>This action cannot be undone.</b>`, "Delete Subscriber");
      if (confirmed) {
        const res = await subSvcRef.current.deleteSubscriber(sub);
        if (res) {
          setSubscribers(prev => prev.filter(s => s.Id !== sub.Id));
          closeDeletedSelection(sub.Id);
        }
      }
    }
  };

  const handleTableItemChange = async (itemid: number, name: keyof ISubscriber, value: any): Promise<void> => {
    if (subSvcRef.current) {
      const sub = subscribers.find(s => s.Id === itemid);

      if (sub) {
        const updatedSub = {
          ...sub,
          [name]: value
        };

        const saveres = await subSvcRef.current.saveSubscriber(updatedSub, false);

        if (saveres) {
          setSubscribers(prev => prev.map(s => (s.Id === saveres.Id ? saveres : s)));
        }
      }
    }
  };

  const handlePropertyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedSub) {
      const { name, value } = event.target;

      if (name) {
        setSelectedSub(prev => ({
          ...prev as ISubscriber,
          [name]: value
        }));
      }
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedSub) {
      const { name, checked } = event.target;

      if (name) {
        setSelectedSub(prev => ({
          ...prev as ISubscriber,
          [name]: checked
        }));
      }
    }
  };

  const closeDetail = (): void => {
    setSelectedSub(null);
    setShowDetail(false);
  };

  const exportSubscribers = (): void => {
    if (subscribers.length > 0) {
      const data = subscribers.map(sub => ({
        Name: sub.Name,
        EmailAddress: sub.EmailAddress
      }));

      const csv = Papa.unparse(data);
      const blob = new Blob([csv]);
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "subscribers.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const createValidator = (): void => {
    if (subFormRef.current && !subFvRef.current) {
      subFvRef.current = createFormValidation(subFormRef.current, {
        fields: {
          Name: {
            validators: {
              notEmpty: { message: "Subscriber name is required" }
            }
          },
          EmailAddress: {
            validators: {
              notEmpty: { message: "Email address is required" },
              emailAddress: { message: "Please enter a valid email address" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger()
        }
      });
    }
  };

  const navToImport = (): void => {
    nav("/subscribers/import");
  };

  useEffect(() => {
    if (subFormRef.current) {
      createValidator();
    } else {
      subFvRef.current = null;
    }
  }, [subFormRef, showDetail]);

  useEffect(() => {
    if (!subSvcRef.current) {
      subSvcRef.current = new SubscriberService();
    }

    const getData = async () => {
      try {
        await getSubscribers();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading subscriber details..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">Subscriber Library</h2>
                </div>
                <div className={styles.headerActions}>
                  <Button variant="outline-primary" className="btn-nomin" title="import subscribers" onClick={navToImport}>
                    <FontAwesomeIcon icon={faFileUpload} />
                    Import
                  </Button>
                  <Button variant="primary" className="btn-nomin" title="add subscriber" onClick={addSubscriber}>
                    <FontAwesomeIcon icon={faPlus} />
                    Add Subscriber
                  </Button>
                </div>
              </div>
            </div>
            <div className="app-list-section__body">
              {subscribers.length > 0 ? (
                <FilteredTable<ISubscriber>
                  columns={columns}
                  data={subscribers}
                  noDataMessage="No subscribers found."
                  showAddButton={true}
                  addButtonText="Export"
                  addButtonIcon={faDownload}
                  addButtonHandler={exportSubscribers}
                  cellClass="align-middle"
                  filterPlaceHolder="filter subscribers..."
                />
              ) : (
                <div className={styles.emptyWrap}>
                  <AppEmptyState
                    title="No subscribers found"
                    message="Add a subscriber manually or import a list to start building the audience attached to this site."
                    compact
                  />
                  <div className={styles.emptyActions}>
                    <Button variant="outline-primary" onClick={navToImport}>
                      <FontAwesomeIcon icon={faFileUpload} />
                      Import Subscribers
                    </Button>
                    <Button variant="primary" onClick={addSubscriber}>
                      <FontAwesomeIcon icon={faPlus} />
                      Add Subscriber
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {showDetail && selectedSub && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Subscriber Details"
          onDataAction={saveSubscriber}>
          <Form id="subscriber-form" ref={subFormRef}>
            <Row>
              <Col md={12}>
                <FormLabel htmlFor="Name">Name</FormLabel>
                <FormControl
                  id="Name"
                  name="Name"
                  type="text"
                  placeholder="subscriber full name..."
                  value={selectedSub.Name}
                  onChange={handlePropertyChange}
                  autoComplete="off"
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel htmlFor="EmailAddress">Email Address</FormLabel>
                <FormControl
                  id="EmailAddress"
                  name="EmailAddress"
                  type="text"
                  placeholder="subscriber email address..."
                  value={selectedSub.EmailAddress}
                  onChange={handlePropertyChange}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={12} md={6} className="mt-3">
                <FormLabel htmlFor="Enabled">Enabled</FormLabel>
                <FormCheckInput
                  className="form-check"
                  id="Enabled"
                  name="Enabled"
                  checked={selectedSub.Enabled}
                  onChange={handleCheckChange}
                />
              </Col>
              <Col sm={12} md={6} className="mt-3">
                <FormLabel htmlFor="Newsletter">Newsletter Sub</FormLabel>
                <FormCheckInput
                  className="form-check"
                  id="Newsletter"
                  name="Newsletter"
                  checked={selectedSub.Newsletter}
                  onChange={handleCheckChange}
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}
    </>
  );
};

export default ManageSubscribers;
